// Duty-time limits for ATO instructors (ported from jupi-fsm-app's
// frontend/src/lib/dutyLimits.ts). Fixed regulatory values, not settings.
// Only the duty-period limits are checked -- they are computable from the
// duty log alone; flight- and working-time limits would need every
// flight from every context.
//
// "In any N consecutive days" is evaluated as of the entry's own date, so
// a back-dated entry is checked against the days around it. Dates are
// YYYY-MM-DD and compared as calendar days, never through a timezone.

export const DUTY_LIMIT_MINUTES = {
  daily: 10 * 60,
  sevenDay: 60 * 60,
  fourteenDay: 110 * 60,
  twentyEightDay: 190 * 60,
} as const

export type DutyWindow = keyof typeof DUTY_LIMIT_MINUTES

export const DUTY_WINDOW_DAYS: Record<DutyWindow, number> = {
  daily: 1,
  sevenDay: 7,
  fourteenDay: 14,
  twentyEightDay: 28,
}

export interface DutyEntry {
  logDate: string
  dutyMinutes: number | null
}

function daysBefore(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() - days)
  return d.toISOString().slice(0, 10)
}

export function dutyMinutesInWindow(entries: DutyEntry[], endDate: string, windowDays: number): number {
  const start = daysBefore(endDate, windowDays - 1)
  return entries
    .filter((e) => e.logDate >= start && e.logDate <= endDate)
    .reduce((sum, e) => sum + (e.dutyMinutes ?? 0), 0)
}

export interface DutyWindowTotal {
  window: DutyWindow
  totalMinutes: number
  limitMinutes: number
  exceeded: boolean
}

// Every window's total as of `date`, with `extraMinutes` added -- the
// entry being typed, when checking before saving.
export function dutyTotals(entries: DutyEntry[], date: string, extraMinutes = 0): DutyWindowTotal[] {
  return (Object.keys(DUTY_LIMIT_MINUTES) as DutyWindow[]).map((window) => {
    const totalMinutes = dutyMinutesInWindow(entries, date, DUTY_WINDOW_DAYS[window]) + extraMinutes
    const limitMinutes = DUTY_LIMIT_MINUTES[window]
    return { window, totalMinutes, limitMinutes, exceeded: totalMinutes > limitMinutes }
  })
}

// Minutes from one wall-clock time to another, through midnight if the
// end is earlier: a duty from 20:00 to 02:00 is six hours.
export function minutesBetween(start: string, end: string): number {
  const [sh = 0, sm = 0] = start.split(':').map(Number)
  const [eh = 0, em = 0] = end.split(':').map(Number)
  const diff = eh * 60 + em - (sh * 60 + sm)
  return diff >= 0 ? diff : diff + 24 * 60
}
